import axios from 'axios'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PatientAccessFormBrowserWarning } from './PatientAccessForm.models'
import { getOutdatedBrowserMinVersion } from '../../utils/outdatedChromium'

interface BrowserWarningContent {
  title: Record<string, string>
  description: Record<string, string>
  minChromeVersion?: number
  minEdgeVersion?: number
}

const getMinimum = (value: number | undefined, fallback: number) =>
  typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : fallback

export const usePatientAccessFormBrowserWarning = (
  fallbackMinVersion: number
): PatientAccessFormBrowserWarning | undefined => {
  const { t, i18n } = useTranslation()
  const [content, setContent] = useState<BrowserWarningContent>()
  const disabled = fallbackMinVersion === 0

  useEffect(() => {
    if (disabled) return

    let active = true
    const controller = new AbortController()

    axios
      .get<BrowserWarningContent>(
        '/content/patient-access-form-browser-warning',
        {
          signal: controller.signal,
          timeout: 25000
        }
      )
      .then(({ data }) => {
        if (active) setContent(data)
      })
      .catch(() => undefined)

    return () => {
      active = false
      controller.abort()
    }
  }, [disabled])

  if (disabled) return undefined

  const minChromeVersion = getMinimum(
    content?.minChromeVersion,
    fallbackMinVersion
  )
  const minEdgeVersion = getMinimum(content?.minEdgeVersion, fallbackMinVersion)
  const outdatedMinimum = getOutdatedBrowserMinVersion(
    minChromeVersion,
    minEdgeVersion
  )

  if (outdatedMinimum === undefined) return undefined

  const versions = {
    chromeVersion: minChromeVersion,
    edgeVersion: minEdgeVersion,
    version: outdatedMinimum
  }
  const interpolate = (text: string) =>
    text.replace(
      /{{\s*(chromeVersion|edgeVersion|version)\s*}}/g,
      (_, key: keyof typeof versions) => String(versions[key])
    )

  for (const locale of [i18n.language, i18n.language.split('-')[0], 'en']) {
    const title = content?.title?.[locale]
    const description = content?.description?.[locale]
    if (
      typeof title === 'string' &&
      title.trim() &&
      typeof description === 'string' &&
      description.trim() &&
      (minChromeVersion === minEdgeVersion ||
        !/{{\s*version\s*}}/.test(`${title} ${description}`))
    ) {
      return {
        title: interpolate(title),
        description: interpolate(description)
      }
    }
  }

  return {
    title: t('patient_access_form:outdated_browser_title'),
    description: t('patient_access_form:outdated_browser_description', versions)
  }
}
